import React, { useState, useRef, useEffect } from 'react'
import { Check, X, Loader2, Calendar } from 'lucide-react'

interface EditableDateProps {
  value: string
  onSave: (value: string) => Promise<void>
  placeholder?: string
  className?: string
}

export default function EditableDate({ 
  value, 
  onSave, 
  placeholder = "Klicken zum Bearbeiten",
  className = ""
}: EditableDateProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Normalize date string to YYYY-MM-DD format (for HTML date input)
  const normalizeDateForInput = (dateStr: string): string => {
    if (!dateStr) return ''
    // If it's an ISO string, extract just the date part
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0]
    }
    // If it's already YYYY-MM-DD, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr
    }
    return dateStr
  }

  useEffect(() => {
    setEditValue(normalizeDateForInput(value))
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = async () => {
    // Compare normalized values to avoid issues with ISO strings
    if (normalizeDateForInput(editValue) === normalizeDateForInput(value)) {
      setIsEditing(false)
      return
    }

    try {
      setIsSaving(true)
      await onSave(editValue)
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving date:', error)
      // Reset to original value on error
      setEditValue(value)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(value)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return placeholder
    try {
      // Handle ISO-String format (YYYY-MM-DDTHH:mm:ss.sssZ) - extract date part only
      let datePart = dateString
      if (dateString.includes('T')) {
        datePart = dateString.split('T')[0]
      }
      
      // Handle pure date format (YYYY-MM-DD)
      const parts = datePart.split('-')
      if (parts.length === 3 && parts[0].length === 4) {
        // Format: YYYY-MM-DD -> DD.MM.YYYY
        const day = parts[2].padStart(2, '0')
        const month = parts[1].padStart(2, '0')
        const year = parts[0]
        return `${day}.${month}.${year}`
      }
      
      // Fallback: return as-is if format is unexpected
      return dateString
    } catch {
      return dateString
    }
  }

  if (isEditing) {
    return (
      <div className="flex items-center space-x-2">
        <input
          ref={inputRef}
          type="date"
          value={normalizeDateForInput(editValue)}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`px-2 py-1 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
          disabled={isSaving}
        />
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return (
    <div 
      onClick={() => setIsEditing(true)}
      className={`cursor-pointer hover:bg-gray-50 rounded px-2 py-1 transition-colors flex items-center justify-center space-x-1 ${className}`}
      title="Klicken zum Bearbeiten"
    >
      <Calendar className="h-4 w-4 text-gray-400" />
      <span>{formatDate(value)}</span>
    </div>
  )
}
